/**
 * Employee Deductions — assign recurring (or one-off) deductions to an employee.
 * Authority: HR_PAYROLL_MANAGE (write) / HR_PAYROLL_VIEW (read)
 *
 * A deduction is recurring while active=true and the period's month falls within
 * [startDate, endDate] (endDate=null = indefinite; startDate==endDate = one-off).
 */
import { useState } from "react";
import { Plus, UserSearch } from "lucide-react";
import { useAuth } from "#/context/AuthProvider";
import { leaveApi } from "../../leave/api";
import type { EmployeeOption } from "../../leave/types";
import { payrollApi } from "../api";
import type { EmployeeDeductionRequest } from "../types";
import { useAsyncData, useMutation } from "../hooks";
import {
  Btn, Card, CardHeader, EmptyState, PageHeader, PayrollError, PayrollLoading,
  SearchInput, Table, TBody, TD, TH, THead, TR, fmt, fmtDate,
} from "../components/PayrollUI";

function empLabel(e: EmployeeOption) {
  return e.fullName || e.employeeName || e.name || e.employeeNumber;
}

export default function PayrollEmployeeDeductionsPage() {
  const { hasRole, hasPermission } = useAuth();
  const isAdmin = hasRole("SUPER_ADMIN") || hasRole("ADMIN");
  const canManage = isAdmin || hasPermission("HR_PAYROLL_MANAGE");

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<EmployeeOption | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<Omit<EmployeeDeductionRequest, "employeeId">>({
    categoryId: 0, amount: 0, startDate: new Date().toISOString().slice(0, 10), endDate: null, notes: "",
  });

  const employees = useAsyncData(() => leaveApi.employeeOptions(), []);
  const categories = useAsyncData(() => payrollApi.listDeductionCategories(), []);
  const deductions = useAsyncData(
    () => (selected ? payrollApi.listEmployeeDeductions(selected.id) : Promise.resolve(null)),
    [selected?.id],
  );

  const create = useMutation((payload: EmployeeDeductionRequest) => payrollApi.createEmployeeDeduction(payload));
  const setActive = useMutation((payload: { id: number; active: boolean }) => payrollApi.setEmployeeDeductionActive(payload.id, payload.active));

  const filtered = (employees.data ?? []).filter((e) =>
    !search || empLabel(e).toLowerCase().includes(search.toLowerCase()) || e.employeeNumber.toLowerCase().includes(search.toLowerCase()),
  );

  async function save() {
    if (!selected || !form.categoryId) return;
    await create.mutate({ employeeId: selected.id, ...form });
    setShowForm(false);
    setForm({ categoryId: 0, amount: 0, startDate: new Date().toISOString().slice(0, 10), endDate: null, notes: "" });
    deductions.reload();
  }

  const inp = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100";

  return (
    <div className="space-y-5">
      <PageHeader
        breadcrumb="Finance · Setup"
        title="Employee Deductions"
      />

      <Card noPad>
        <CardHeader title="Select Employee" />
        <div className="p-5 space-y-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Search by name or employee number…" />
          {employees.loading && <PayrollLoading />}
          {employees.error && <PayrollError message={employees.error} onRetry={employees.reload} />}
          {!employees.loading && !employees.error && (
            <div className="max-h-56 overflow-y-auto rounded-xl border border-slate-100">
              {filtered.length === 0 ? (
                <div className="p-4 text-sm text-slate-400">No employees match.</div>
              ) : filtered.slice(0, 50).map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => setSelected(e)}
                  className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition hover:bg-slate-50 ${selected?.id === e.id ? "bg-indigo-50" : ""}`}
                >
                  <span className="font-medium text-slate-800">{empLabel(e)}</span>
                  <span className="font-mono text-xs text-slate-400">{e.employeeNumber}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>

      {selected && (
        <Card noPad>
          <CardHeader
            title={`Deductions — ${empLabel(selected)}`}
            subtitle={selected.employeeNumber}
            action={canManage && <Btn size="sm" icon={Plus} onClick={() => setShowForm((s) => !s)}>Add Deduction</Btn>}
          />

          {showForm && canManage && (
            <div className="border-b p-5">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <label className="block text-xs font-semibold text-slate-500">
                  Category
                  <select className={`mt-1 ${inp}`} value={form.categoryId} onChange={(e) => setForm((f) => ({ ...f, categoryId: Number(e.target.value) }))}>
                    <option value={0}>Select…</option>
                    {(categories.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
                <label className="block text-xs font-semibold text-slate-500">
                  Amount (KES)
                  <input type="number" className={`mt-1 ${inp}`} value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: Number(e.target.value) }))} />
                </label>
                <label className="block text-xs font-semibold text-slate-500">
                  Start Date
                  <input type="date" className={`mt-1 ${inp}`} value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} />
                </label>
                <label className="block text-xs font-semibold text-slate-500">
                  End Date (blank = indefinite)
                  <input type="date" className={`mt-1 ${inp}`} value={form.endDate ?? ""} onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value || null }))} />
                </label>
                <label className="block text-xs font-semibold text-slate-500 sm:col-span-2 lg:col-span-4">
                  Notes
                  <input className={`mt-1 ${inp}`} value={form.notes ?? ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
                </label>
              </div>
              {create.error && <p className="mt-3 text-sm text-red-600">{create.error}</p>}
              <div className="mt-4 flex justify-end gap-2">
                <Btn onClick={() => setShowForm(false)}>Cancel</Btn>
                <Btn variant="primary" loading={create.loading} loadingText="Saving…" disabled={!form.categoryId || !form.amount} onClick={save}>Save</Btn>
              </div>
            </div>
          )}

          {deductions.error && <div className="p-5"><PayrollError message={deductions.error} onRetry={deductions.reload} /></div>}
          {deductions.loading ? <PayrollLoading /> : !deductions.error && (
            <Table>
              <THead><TH>Category</TH><TH right>Amount</TH><TH>Start</TH><TH>End</TH><TH>Status</TH><TH>Notes</TH><TH right>Actions</TH></THead>
              <TBody>
                {!(deductions.data?.content.length) ? (
                  <tr><td colSpan={7}><EmptyState icon={UserSearch} title="No deductions assigned" /></td></tr>
                ) : deductions.data!.content.map((d) => (
                  <TR key={d.id}>
                    <TD className="font-semibold text-slate-800">{d.categoryName ?? d.categoryId}</TD>
                    <TD right>{fmt(d.amount)}</TD>
                    <TD muted>{fmtDate(d.startDate)}</TD>
                    <TD muted>{d.endDate ? fmtDate(d.endDate) : "Indefinite"}</TD>
                    <TD>{d.active
                      ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Active</span>
                      : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">Inactive</span>}
                    </TD>
                    <TD muted>{d.notes ?? "—"}</TD>
                    <TD right>
                      {canManage && (
                        <Btn size="sm" variant={d.active ? "dangerOutline" : "emerald"} loading={setActive.loading}
                          onClick={() => setActive.mutate({ id: d.id, active: !d.active }).then(deductions.reload)}>
                          {d.active ? "Deactivate" : "Activate"}
                        </Btn>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Card>
      )}
    </div>
  );
}
